import React from 'react';
import PersonalisationSummaryModal from '../PersonalisationSummaryModal';

import { useViewModel } from './useViewModel';

import styles from './personalisation-footer.module.css';

/**
 * PersonalisationFooter component displays the footer for the personalisation journey, including navigation and toast notifications.
 *
 * @param {Object} props - Component props
 * @param {number} props.currentStep - The current step index
 * @param {number} props.totalSteps - The total number of steps
 * @param {function} props.onPrev - Callback for previous step
 * @param {function} props.onNext - Callback for next step
 * @param {function} props.onClose - Callback for closing the modal
 * @returns {import('react').JSX.Element}
 */
export function PersonalisationFooter(props) {
  const {
    toastClassName,
    showToast,
    toastType,
    handleSummaryModalOpen,
    handleSummaryModalClose,
    summaryModalOpen,
    handlePrev,
    handleNext,
    finishPersonalisation,
    currentStep,
    currentTotalPrice,
    isLastStep,
    isStepValid,
    formatPrice,
    getButtonText
  } = useViewModel(props);

  return (
    <div className={styles['personalisation__footer']}>
      <div className={styles['personalisation__footer-inner']}>
        <div className={styles['personalisation__footer-detail']}>
          <p className={styles['personalisation__footer-detail-label'] + ' body-base'}>Current Total:</p>
          <p className={styles['personalisation__footer-detail-price'] + ' body-lg font-medium'}>
            {formatPrice({ amount: currentTotalPrice })}
          </p>
          <button
            className={styles['personalisation__footer-summary-control'] + ' button-reset'}
            onClick={handleSummaryModalOpen}
          >
            <span className="svg-wrapper">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none">
                <path
                  fill="currentColor"
                  fillRule="evenodd"
                  d="M15.294 7.205a.417.417 0 0 1 0 .59l-5 5a.417.417 0 0 1-.589 0l-5-5a.417.417 0 1 1 .59-.59L10 11.911l4.705-4.706a.417.417 0 0 1 .59 0Z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
          </button>
          <PersonalisationSummaryModal isOpen={summaryModalOpen} onClose={handleSummaryModalClose} />
        </div>
        <div className={styles['personalisation__footer-actions']}>
          {currentStep > 0 && (
            <button
              className="button button--secondary button--black button--icon-only"
              onClick={handlePrev}
              aria-label="Back"
            >
              <span className="svg-wrapper">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M7.79459 6.1278C7.95731 5.96508 7.95731 5.70126 7.79459 5.53854C7.63187 5.37583 7.36805 5.37582 7.20533 5.53854L3.03867 9.70516C2.95727 9.78655 2.91659 9.89324 2.91663 9.99992C2.91663 9.99987 2.91663 9.99996 2.91663 9.99992C2.91663 10.1194 2.96691 10.2272 3.04746 10.3032L7.20529 14.4612C7.36801 14.6239 7.63183 14.6239 7.79455 14.4612C7.95727 14.2985 7.95727 14.0346 7.79456 13.8719L4.33945 10.4167H16.6666C16.8967 10.4167 17.0833 10.2302 17.0833 10C17.0833 9.76992 16.8967 9.58337 16.6666 9.58337H4.33897L7.79459 6.1278Z"
                    fill="currentColor"
                  />
                </svg>
              </span>
            </button>
          )}
          {!isLastStep ? (
            <button
              className={styles['personalisation__footer-continue'] + ' button button--primary button--orange'}
              onClick={handleNext}
              disabled={!isStepValid}
              title={!isStepValid ? 'Please complete all required selections to continue' : ''}
            >
              {getButtonText()}
            </button>
          ) : (
            <>
              {/* @TODO: Add functionality of building up URL with personalisation choices before we show the share design button */}
              {/* <button
                className="button button--secondary button--black button--icon-only"
                onClick={handleShareDesign}
                aria-label="Share design"
              >
                <span className="svg-wrapper">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M17.5 5.41675C17.5 6.79746 16.3807 7.91675 15 7.91675C13.6192 7.91675 12.5 6.79746 12.5 5.41675C12.5 4.03604 13.6192 2.91675 15 2.91675C16.3807 2.91675 17.5 4.03604 17.5 5.41675Z"
                      stroke="currentColor"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M7.5 10C7.5 11.3807 6.38071 12.5 5 12.5C3.61929 12.5 2.5 11.3807 2.5 10C2.5 8.61925 3.61929 7.5 5 7.5C6.38071 7.5 7.5 8.61925 7.5 10Z"
                      stroke="currentColor"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M17.5 14.5833C17.5 15.964 16.3807 17.0833 15 17.0833C13.6192 17.0833 12.5 15.964 12.5 14.5833C12.5 13.2025 13.6192 12.0833 15 12.0833C16.3807 12.0833 17.5 13.2025 17.5 14.5833Z"
                      stroke="currentColor"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M7.0835 8.74958L12.5002 6.25M7.0835 10.8333L12.5002 13.3329"
                      stroke="currentColor"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </button> */}
              <button
                className={styles['personalisation__footer-atb'] + ' button button--primary button--orange'}
                onClick={finishPersonalisation}
                disabled={!isStepValid}
                title={!isStepValid ? 'Please complete all required selections to add to bag' : ''}
              >
                {getButtonText()}
              </button>
            </>
          )}
        </div>
      </div>
      {showToast && (
        <div className={toastClassName}>
          {toastType === 'success' ? (
            <>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M10.9742 6.2563C11.1156 6.08659 11.0927 5.83437 10.923 5.69294C10.7533 5.55152 10.5011 5.57445 10.3596 5.74416L7.30671 9.40767L5.61644 7.71739C5.46023 7.56118 5.20696 7.56118 5.05075 7.71739C4.89454 7.8736 4.89454 8.12686 5.05075 8.28307L7.05075 10.2831C7.13022 10.3625 7.23945 10.4049 7.35172 10.3998C7.46399 10.3947 7.56894 10.3426 7.64088 10.2563L10.9742 6.2563Z"
                  fill="currentColor"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8.00026 0.933594C4.09745 0.933594 0.933594 4.09745 0.933594 8.00026C0.933594 11.903 4.09745 15.0669 8.00026 15.0669C11.903 15.0669 15.0669 11.903 15.0669 8.00026C15.0669 4.09745 11.903 0.933594 8.00026 0.933594ZM1.73359 8.00026C1.73359 4.53927 4.53927 1.73359 8.00026 1.73359C11.4612 1.73359 14.2669 4.53928 14.2669 8.00026C14.2669 11.4612 11.4612 14.2669 8.00026 14.2669C4.53928 14.2669 1.73359 11.4612 1.73359 8.00026Z"
                  fill="currentColor"
                />
              </svg>
              Link copied successfully
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M5.95025 5.38414C5.79405 5.22792 5.54079 5.22791 5.38457 5.38411C5.22835 5.54031 5.22834 5.79358 5.38454 5.9498L7.43508 8.00056L5.38504 10.0508C5.22884 10.207 5.22885 10.4603 5.38507 10.6165C5.54128 10.7727 5.79455 10.7727 5.95075 10.6165L8.00073 8.56627L10.0507 10.6165C10.2069 10.7727 10.4602 10.7727 10.6164 10.6165C10.7726 10.4603 10.7726 10.207 10.6164 10.0508L8.56639 8.00056L10.6169 5.9498C10.7731 5.79358 10.7731 5.54031 10.6169 5.38411C10.4607 5.22791 10.2074 5.22792 10.0512 5.38414L8.00073 7.43484L5.95025 5.38414Z"
                  fill="currentColor"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8.00026 0.933594C4.09745 0.933594 0.933594 4.09745 0.933594 8.00026C0.933594 11.903 4.09745 15.0669 8.00026 15.0669C11.903 15.0669 15.0669 11.903 15.0669 8.00026C15.0669 4.09745 11.903 0.933594 8.00026 0.933594ZM1.73359 8.00026C1.73359 4.53927 4.53927 1.73359 8.00026 1.73359C11.4612 1.73359 14.2669 4.53928 14.2669 8.00026C14.2669 11.4612 11.4612 14.2669 8.00026 14.2669C4.53928 14.2669 1.73359 11.4612 1.73359 8.00026Z"
                  fill="currentColor"
                />
              </svg>
              Failed to copy link
            </>
          )}
        </div>
      )}
    </div>
  );
}
